import axios from 'axios';
import { notify } from './notificationService';

export const API_URL =
  (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
    ? 'http://localhost:8000/api'
    : (process.env.REACT_APP_API_URL || 'https://api.resellerbazzar.in/api');
export const STATIC_IMAGE_URL = process.env.REACT_APP_IMAGE_HOST || 'https://reseller-bazzar-media-india.s3.ap-south-1.amazonaws.com/uploads';

const axiosInstance = axios.create({
  baseURL: API_URL,
});

// ── Global success/error notifications ───────────────────────────────────────
// Every mutating call (POST/PUT/PATCH/DELETE) shows a success notification, and
// every failed call shows an error one — both as an in-app toast AND an OS/mobile
// notification (system: true) when the tab is in the background. This gives
// blanket coverage so individual screens don't each have to wire it up.
//
// Opt out per request with `{ skipNotify: true }` (whole call), or
// `{ skipNotify: 'success' }` / `{ skipNotify: 'error' }` to silence just one
// side. Use this for read-style POSTs (e.g. live previews, polling) that would
// otherwise spam success toasts.
const MUTATING = ['post', 'put', 'patch', 'delete'];
const isMutation = (m) => MUTATING.includes(String(m || '').toLowerCase());

// Pull the most human message out of a response/error body, else a fallback.
const pickMessage = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data === 'string') return data.slice(0, 200) || fallback;
  return data.msg || data.message || data.error || fallback;
};
const defaultSuccess = (method) => {
  switch (String(method || '').toLowerCase()) {
    case 'delete': return 'Deleted successfully';
    case 'post': return 'Created successfully';
    default: return 'Saved successfully';
  }
};
const skips = (cfg, side) => cfg?.skipNotify === true || cfg?.skipNotify === side;

// Add a request interceptor to include the token in headers
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
      config.headers['x-auth-token'] = token; // Keeping compatibility with some backends
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor: success notifications + token expiry + error notifications
axiosInstance.interceptors.response.use(
  (response) => {
    try {
      const cfg = response.config || {};
      if (isMutation(cfg.method) && !skips(cfg, 'success')) {
        notify({
          title: 'Success',
          message: pickMessage(response.data, defaultSuccess(cfg.method)),
          severity: 'success',
          system: true,
        });
      }
    } catch (e) { /* never let a notification break the response */ }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      if (window.location.pathname !== '/signin') {
        try { notify({ title: 'Session expired', message: 'Please sign in again.', severity: 'warning', system: true }); } catch (e) { /* ignore */ }
        window.location.href = '/signin';
      }
      return Promise.reject(error);
    }
    try {
      const cfg = error.config || {};
      const canceled = error.code === 'ERR_CANCELED' || (axios.isCancel && axios.isCancel(error));
      if (!canceled && !skips(cfg, 'error')) {
        notify({
          title: 'Action failed',
          message: pickMessage(error.response?.data, error.message || 'Something went wrong. Please try again.'),
          severity: 'error',
          system: true,
        });
      }
    } catch (e) { /* never let a notification break error propagation */ }
    return Promise.reject(error);
  }
);

export default axiosInstance;
